import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { fetchProjectsForPlaneamiento, nombreProyectoDesdeLista } from '../../../utils/projectsFromApi';
import { getMatrizResuelta, formatearRangoFechas, etiquetaSemana } from '../../../data/mockCiclosCiclicos';
import {
  fetchCiclico,
  fetchDatosPlaneamientoCiclicos,
  confirmarSalidaProduccion as confirmarSalidaProduccionApi,
  revertirSalidaProduccion as revertirSalidaProduccionApi,
} from '../../../services/ciclicoService';
import recetaPlatoService from '../../../services/recetaPlatoService';
import entryGuideService from '../../../services/entryGuideService';
import { productService } from '../../../services/productService';
import { getWarehouses } from '../../../services/warehousesService';
import concesionariaWarehouseConfigService from '../../../services/concesionariaWarehouseConfigService';
import {
  ESTADO_SALIDA,
  ESTADO_LABEL,
  parseLineasCelda,
  fechasPorDiaDesdeInicio,
  formatearDiaCorto,
  buscarRecetaPlatoEnCatalogo,
  costoBaseRecetaPlatoApi,
  lineasInsumoEscaladosRecetaApi,
  formatearCantidadReceta4Dec,
  slotKey,
  loadSalidasProduccion,
  upsertSalidaDia,
  getSalidaPorCicloYDia,
  esDiaKeyValido,
  DIAS_LABEL,
  buildResumenInsumosSalidaApi,
  buildResumenPlatosPorCelda,
} from '../../../data/mockSalidasProduccion';
const basePath = '/produccion-concesionaria/operaciones/salidas-produccion';

const fmtMoney = (n) => {
  const x = Number(n);
  const v = Number.isFinite(x) ? x : 0;
  return v.toLocaleString('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 });
};

function costoTotalCeldaPlatosKardex(cantidadStr, textoCelda, catalogoRecetas, recetasFullById, stockCostByProduct) {
  const partes = parseLineasCelda(textoCelda);
  const n = Math.max(0, parseInt(String(cantidadStr || '').replace(/\D/g, ''), 10) || 0);
  if (n === 0 || partes.length === 0) return 0;
  const m = recetasFullById instanceof Map ? recetasFullById : null;
  let sum = 0;
  for (const nombrePlato of partes) {
    const light = buscarRecetaPlatoEnCatalogo(nombrePlato, catalogoRecetas);
    if (!light?.id) continue;
    const receta = m?.get(String(light.id)) || light;
    const ings = lineasInsumoEscaladosRecetaApi(receta, n);
    for (const row of ings) {
      const pid = row.product_id != null ? String(row.product_id) : null;
      const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[row.product_id]) : null;
      const cUnit = Number(cached?.average_cost ?? row.precioUnitario ?? 0);
      const safe = Number.isFinite(cUnit) ? cUnit : 0;
      sum += (Number(row.quantity || 0) * safe);
    }
  }
  return Math.round(sum * 100) / 100;
}

function horaActualHHmm() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const HORA_CORTE_REPORTE = '12:00';
const normalizarHoraHHmm = (raw, fallback = HORA_CORTE_REPORTE) => {
  const t = String(raw || '').trim();
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return fallback;
  const hh = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const mm = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const SalidasProduccionDetalle = () => {
  const { cicloId: cicloIdParam, diaKey: diaKeyParam } = useParams();
  const cicloId = cicloIdParam ? decodeURIComponent(cicloIdParam) : '';
  const diaKey = diaKeyParam ? decodeURIComponent(diaKeyParam).toLowerCase() : '';

  const [ciclo, setCiclo] = useState(null);
  const [cicloCargando, setCicloCargando] = useState(true);
  const [plantillas, setPlantillas] = useState([]);
  const [tiempos, setTiempos] = useState([]);
  const [tiposPlato, setTiposPlato] = useState([]);
  const [proyectosCatalog, setProyectosCatalog] = useState([]);
  const [recetasCatalogo, setRecetasCatalogo] = useState([]);
  const [recetasFullById, setRecetasFullById] = useState(() => new Map());
  const [recetasDetalleCargando, setRecetasDetalleCargando] = useState(false);

  /** Catálogo + tiempos + plantillas + cíclico en la menor cantidad de peticiones posible. */
  useEffect(() => {
    let ok = true;
    (async () => {
      if (!cicloId) {
        setCiclo(null);
        setCicloCargando(false);
        return;
      }
      setCicloCargando(true);
      setCiclo(null);
      try {
        const [bundle, proj] = await Promise.all([fetchDatosPlaneamientoCiclicos(), fetchProjectsForPlaneamiento()]);
        if (!ok) return;
        setProyectosCatalog(proj);
        setPlantillas(bundle.plantillas || []);
        setTiempos((bundle.tiempos || []).filter((x) => x.active !== false));
        setTiposPlato((bundle.tiposPlato || []).filter((x) => x.active !== false));
        setRecetasCatalogo(Array.isArray(bundle.recetas) ? bundle.recetas : []);
        const enBundle = (bundle.ciclos || []).find((c) => String(c.id) === String(cicloId));
        if (enBundle) {
          setCiclo(enBundle);
        } else {
          const c = await fetchCiclico(cicloId);
          if (ok) setCiclo(c);
        }
      } catch {
        if (!ok) return;
        setPlantillas([]);
        setTiempos([]);
        setTiposPlato([]);
        setRecetasCatalogo([]);
        try {
          const [proj, c] = await Promise.all([fetchProjectsForPlaneamiento(), fetchCiclico(cicloId)]);
          if (ok) {
            setProyectosCatalog(proj);
            setCiclo(c);
          }
        } catch {
          if (ok) {
            setProyectosCatalog([]);
            setCiclo(null);
          }
        }
      } finally {
        if (ok) setCicloCargando(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [cicloId]);

  const nombreProyecto = useCallback(
    (pid) => nombreProyectoDesdeLista(pid, proyectosCatalog),
    [proyectosCatalog],
  );

  const plantilla = useMemo(
    () => plantillas.find((p) => String(p.id) === String(ciclo?.plantillaId)) || null,
    [plantillas, ciclo],
  );

  const { matriz, grupos } = useMemo(() => {
    if (!ciclo || !plantilla) return { matriz: {}, grupos: [] };
    return getMatrizResuelta(ciclo, plantilla);
  }, [ciclo, plantilla]);

  const fechasDia = useMemo(() => {
    if (!ciclo?.fechaInicio) return {};
    return fechasPorDiaDesdeInicio(ciclo.fechaInicio);
  }, [ciclo]);

  const tiempoLabel = useCallback(
    (id) => tiempos.find((t) => String(t.id) === String(id))?.label || id,
    [tiempos],
  );
  const tipoLabel = useCallback(
    (id) => tiposPlato.find((t) => String(t.id) === String(id))?.name || id,
    [tiposPlato],
  );

  const [estado, setEstado] = useState(ESTADO_SALIDA.PRESUPUESTADO);
  const [cantidades, setCantidades] = useState({});
  /** Tras Guardar en presupuestado (o salida): cantidades no editables hasta "Editar cantidades". */
  const [bloquearCantidades, setBloquearCantidades] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  const [modalSalidaAbierto, setModalSalidaAbierto] = useState(false);
  const [insumoDetalleModal, setInsumoDetalleModal] = useState(null);
  const [horaSalida, setHoraSalida] = useState(HORA_CORTE_REPORTE);
  const [almacenSalidaId, setAlmacenSalidaId] = useState('');
  const [warehousesOut, setWarehousesOut] = useState([]);
  const [warehouseDestinationByPrincipal, setWarehouseDestinationByPrincipal] = useState({});
  const [productUnitById, setProductUnitById] = useState({});
  const [loadingWarehouseStock, setLoadingWarehouseStock] = useState(false);
  const [confirmandoSalida, setConfirmandoSalida] = useState(false);
  const [stockCostByProduct, setStockCostByProduct] = useState({});
  const [salidaGuideId, setSalidaGuideId] = useState(null);
  const [entradaGuideId, setEntradaGuideId] = useState(null);

  const defaultAlmacenId = warehousesOut[0]?.id ?? '';
  const destinoAlmacenSeleccionado = useMemo(() => {
    const key = String(almacenSalidaId || defaultAlmacenId || '');
    return key ? (warehouseDestinationByPrincipal[key] || null) : null;
  }, [almacenSalidaId, defaultAlmacenId, warehouseDestinationByPrincipal]);

  const cantidadesEditables = !bloquearCantidades;

  const diaValidoEnPlantilla = plantilla?.diasSemana?.[diaKey];
  const ymd = fechasDia[diaKey];
  const diaNombre = DIAS_LABEL[diaKey] || diaKey;

  useEffect(() => {
    if (!cicloId || !esDiaKeyValido(diaKey)) return;
    const list = loadSalidasProduccion();
    const salida = getSalidaPorCicloYDia(cicloId, diaKey, list);
    if (salida) {
      setEstado(salida.estado);
      setCantidades(salida.cantidades && typeof salida.cantidades === 'object' ? { ...salida.cantidades } : {});
      setBloquearCantidades(salida.bloquearCantidades === true);
      setHoraSalida(normalizarHoraHHmm(salida.horaSalidaProduccion, HORA_CORTE_REPORTE));
      setSalidaGuideId(salida.salidaGuideId ?? null);
      setEntradaGuideId(salida.entradaGuideId ?? null);
    } else {
      setEstado(ESTADO_SALIDA.PRESUPUESTADO);
      setCantidades({});
      setBloquearCantidades(false);
      setHoraSalida(HORA_CORTE_REPORTE);
      setSalidaGuideId(null);
      setEntradaGuideId(null);
    }
    setExpanded(new Set());
    setModalSalidaAbierto(false);
  }, [cicloId, diaKey]);

  const slots = useMemo(() => {
    if (!plantilla || !grupos.length || !esDiaKeyValido(diaKey) || !plantilla.diasSemana?.[diaKey]) {
      return [];
    }
    const rows = [];
    grupos.forEach((g) => {
      const tLabel = tiempoLabel(g.tiempoId);
      g.filas.forEach((f) => {
        const texto = matriz[diaKey]?.[f.rowKey] ?? '';
        rows.push({
          rowKey: f.rowKey,
          tiempoId: g.tiempoId,
          tiempoLabel: tLabel,
          tipoPlatoId: f.tipoPlatoId,
          tipoLabel: tipoLabel(f.tipoPlatoId),
          texto,
          sk: slotKey(diaKey, f.rowKey),
        });
      });
    });
    return rows;
  }, [plantilla, grupos, matriz, diaKey, tiempoLabel, tipoLabel]);

  useEffect(() => {
    let cancel = false;
    if (!slots.length || !recetasCatalogo.length) {
      setRecetasFullById(new Map());
      setRecetasDetalleCargando(false);
      return undefined;
    }
    setRecetasDetalleCargando(true);
    (async () => {
      const m = new Map();
      const toFetch = new Set();
      for (const slot of slots) {
        for (const parte of parseLineasCelda(slot.texto)) {
          const light = buscarRecetaPlatoEnCatalogo(parte, recetasCatalogo);
          if (!light?.id) continue;
          const idStr = String(light.id);
          if (Array.isArray(light.items) && light.items.length > 0) m.set(idStr, light);
          else toFetch.add(idStr);
        }
      }
      for (const idStr of toFetch) {
        if (m.has(idStr)) continue;
        try {
          const res = await recetaPlatoService.get(idStr);
          if (res?.success && res.data) m.set(idStr, res.data);
        } catch {
          /* receta no disponible: costo 0 */
        }
      }
      if (!cancel) {
        setRecetasFullById(m);
        setRecetasDetalleCargando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slots, recetasCatalogo, cicloId, diaKey]);

  const totalPresupuesto = useMemo(() => {
    let t = 0;
    slots.forEach((slot) => {
      const q = cantidades[slot.sk];
      t += costoTotalCeldaPlatosKardex(q, slot.texto, recetasCatalogo, recetasFullById, stockCostByProduct);
    });
    return Math.round(t * 100) / 100;
  }, [slots, cantidades, recetasCatalogo, recetasFullById, stockCostByProduct]);

  const totalPlatosDia = useMemo(() => {
    let t = 0;
    for (const slot of slots) {
      t += Math.max(0, parseInt(String(cantidades[slot.sk] || '').replace(/\D/g, ''), 10) || 0);
    }
    return t;
  }, [slots, cantidades]);

  const costoUnitarioPlatoDia = useMemo(() => {
    if (totalPlatosDia <= 0) return 0;
    return Math.round((totalPresupuesto / totalPlatosDia) * 100) / 100;
  }, [totalPresupuesto, totalPlatosDia]);

  const resumenInsumos = useMemo(
    () => buildResumenInsumosSalidaApi(slots, cantidades, recetasCatalogo, recetasFullById),
    [slots, cantidades, recetasCatalogo, recetasFullById],
  );
  const resumenInsumosKardex = useMemo(() => {
    const grouped = new Map();
    (resumenInsumos.filas || []).forEach((row) => {
      const pid = row.product_id != null ? String(row.product_id) : null;
      const canonicalUnit = pid ? (productUnitById[pid] || row.unit || '') : (row.unit || '');
      const key = pid ? `id:${pid}|||${canonicalUnit}` : `name:${row.product}|||${canonicalUnit}`;
      const prev = grouped.get(key) || {
        ...row,
        unit: canonicalUnit || row.unit,
        quantity: 0,
      };
      prev.quantity = Math.round((Number(prev.quantity || 0) + Number(row.quantity || 0)) * 10000) / 10000;
      grouped.set(key, prev);
    });
    const filas = Array.from(grouped.values()).map((row) => {
      const pid = row.product_id != null ? String(row.product_id) : null;
      const cached = pid ? (stockCostByProduct[pid] || stockCostByProduct[row.product_id]) : null;
      const cUnit = Number(cached?.average_cost ?? 0);
      const stock = cached?.total_stock != null ? Number(cached.total_stock) : null;
      const subtotal = Math.round((Number(row.quantity || 0) * (Number.isFinite(cUnit) ? cUnit : 0)) * 100) / 100;
      return {
        ...row,
        costoUnitarioKardex: Number.isFinite(cUnit) ? cUnit : 0,
        stockKardex: Number.isFinite(stock) ? stock : null,
        subtotalKardex: subtotal,
      };
    });
    const totalCosto = Math.round(filas.reduce((s, r) => s + (Number(r.subtotalKardex) || 0), 0) * 100) / 100;
    return { filas, totalCosto };
  }, [resumenInsumos, stockCostByProduct, productUnitById]);

  const resumenPlatosCeldas = useMemo(
    () => buildResumenPlatosPorCelda(slots, cantidades),
    [slots, cantidades],
  );
  const resumenPlatosTerminados = useMemo(() => {
    const map = new Map();
    for (const slot of slots) {
      const n = Math.max(0, parseInt(String(cantidades?.[slot.sk] || '').replace(/\D/g, ''), 10) || 0);
      if (n <= 0) continue;
      for (const nombrePlato of parseLineasCelda(slot.texto)) {
        const light = buscarRecetaPlatoEnCatalogo(nombrePlato, recetasCatalogo);
        if (!light?.id) continue;
        const receta = (recetasFullById instanceof Map ? recetasFullById.get(String(light.id)) : null) || light;
        const productId = receta?.product_id;
        if (!productId) continue;
        const insumosReceta = lineasInsumoEscaladosRecetaApi(receta, n);
        const costoTotalPlato = Math.round(insumosReceta.reduce((acc, ing) => {
          const pid = ing.product_id != null ? String(ing.product_id) : null;
          const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[ing.product_id]) : null;
          const cUnit = Number(cached?.average_cost ?? ing.precioUnitario ?? 0);
          const safe = Number.isFinite(cUnit) ? cUnit : 0;
          return acc + (Number(ing.quantity || 0) * safe);
        }, 0) * 100) / 100;
        const key = String(productId);
        const prev = map.get(key) || {
          product_id: Number(productId),
          product: receta?.name || light?.name || nombrePlato,
          quantity: 0,
          totalCost: 0,
        };
        prev.quantity += n;
        prev.totalCost = Math.round((Number(prev.totalCost || 0) + costoTotalPlato) * 100) / 100;
        map.set(key, prev);
      }
    }
    const rows = Array.from(map.values()).map((row) => {
      const qty = Number(row.quantity || 0);
      const totalCost = Number(row.totalCost || 0);
      const unitPrice = qty > 0 ? Math.round((totalCost / qty) * 10000) / 10000 : 0;
      return {
        ...row,
        totalCost: Math.round(totalCost * 100) / 100,
        unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
      };
    });
    const qtyTotal = rows.reduce((acc, r) => acc + (Number(r.quantity) || 0), 0);
    return { rows, qtyTotal };
  }, [slots, cantidades, recetasCatalogo, recetasFullById, stockCostByProduct]);
  const desgloseInsumoPorPlato = useMemo(() => {
    const out = new Map();
    for (const slot of slots) {
      const n = Math.max(0, parseInt(String(cantidades?.[slot.sk] || '').replace(/\D/g, ''), 10) || 0);
      if (n <= 0) continue;
      for (const nombrePlato of parseLineasCelda(slot.texto)) {
        const light = buscarRecetaPlatoEnCatalogo(nombrePlato, recetasCatalogo);
        if (!light?.id) continue;
        const full = (recetasFullById instanceof Map ? recetasFullById.get(String(light.id)) : null) || light;
        const rows = lineasInsumoEscaladosRecetaApi(full, n);
        for (const row of rows) {
          const pid = row.product_id != null ? String(row.product_id) : null;
          const canonicalUnit = pid ? (productUnitById[pid] || row.unit || '') : (row.unit || '');
          const key = pid ? `id:${pid}|||${canonicalUnit}` : `name:${row.product}|||${canonicalUnit}`;
          const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[row.product_id]) : null;
          const cUnit = Number(cached?.average_cost ?? row.precioUnitario ?? 0);
          const safe = Number.isFinite(cUnit) ? cUnit : 0;
          const subtotal = Math.round((Number(row.quantity || 0) * safe) * 100) / 100;
          if (!out.has(key)) out.set(key, []);
          out.get(key).push({
            tiempo: slot.tiempoLabel,
            tipo: slot.tipoLabel,
            menu: slot.texto,
            plato: nombrePlato,
            quantity: Number(row.quantity || 0),
            unit: canonicalUnit || row.unit,
            cUnit: safe,
            subtotal,
          });
        }
      }
    }
    for (const [k, list] of out.entries()) {
      list.sort((a, b) => {
        const byTiempo = String(a.tiempo).localeCompare(String(b.tiempo), 'es');
        if (byTiempo !== 0) return byTiempo;
        return String(a.plato).localeCompare(String(b.plato), 'es');
      });
      out.set(k, list);
    }
    return out;
  }, [slots, cantidades, recetasCatalogo, recetasFullById, stockCostByProduct, productUnitById]);

  const setCantidad = (sk, value) => {
    if (!cantidadesEditables) return;
    const cleaned = String(value).replace(/[^\d]/g, '');
    setCantidades((prev) => ({ ...prev, [sk]: cleaned }));
  };

  const toggleExpand = (sk) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(sk)) n.delete(sk);
      else n.add(sk);
      return n;
    });
  };

  const guardar = () => {
    if (!cicloId || !esDiaKeyValido(diaKey)) return;
    const bloquear =
      estado === ESTADO_SALIDA.PRESUPUESTADO || estado === ESTADO_SALIDA.SALIDA_PRODUCCION;
    upsertSalidaDia({
      cicloId,
      diaKey,
      estado,
      cantidades,
      bloquearCantidades: bloquear,
    });
    setBloquearCantidades(bloquear);
    setToast({ type: 'ok', message: 'Salida del día guardada correctamente.' });
    setTimeout(() => setToast(null), 3500);
  };

  const habilitarEdicionCantidades = () => {
    if (!cicloId || !esDiaKeyValido(diaKey)) return;
    upsertSalidaDia({
      cicloId,
      diaKey,
      estado,
      cantidades,
      bloquearCantidades: false,
    });
    setBloquearCantidades(false);
    setToast({ type: 'ok', message: 'Puede modificar las cantidades.' });
    setTimeout(() => setToast(null), 3000);
  };

  const eliminarSalidaProduccion = async () => {
    if (!cicloId || !esDiaKeyValido(diaKey)) return;
    const ok = window.confirm('Se eliminará la salida a producción y el día volverá a estado presupuestado. ¿Desea continuar?');
    if (!ok) return;
    let relationNotFound = false;
    try {
      await revertirSalidaProduccionApi({
        salida_guide_id: salidaGuideId != null ? Number(salidaGuideId) : null,
        entrada_guide_id: entradaGuideId != null ? Number(entradaGuideId) : null,
        ciclo_id: cicloId,
        dia_key: diaKey,
      });
    } catch (err) {
      const msg = String(err?.message || '').toLowerCase();
      // Para registros antiguos sin relación persistida, permitimos volver a presupuestado
      // desde cíclicos sin bloquear al usuario.
      if (msg.includes('no se encontró relación de guías')) {
        relationNotFound = true;
      } else {
        setToast({ type: 'error', message: err?.message || 'No se pudo revertir la salida a producción.' });
        setTimeout(() => setToast(null), 4000);
        return;
      }
    }
    setEstado(ESTADO_SALIDA.PRESUPUESTADO);
    upsertSalidaDia({
      cicloId,
      diaKey,
      estado: ESTADO_SALIDA.PRESUPUESTADO,
      cantidades,
      bloquearCantidades: true,
      horaSalidaProduccion: '',
      salidaGuideId: null,
      entradaGuideId: null,
    });
    setBloquearCantidades(true);
    setHoraSalida(HORA_CORTE_REPORTE);
    setSalidaGuideId(null);
    setEntradaGuideId(null);
    setToast({
      type: relationNotFound ? 'warning' : 'ok',
      message: relationNotFound
        ? 'Salida revertida en cíclicos (estado Presupuestado). No se encontró relación de guías para borrar en BD.'
        : 'Salida a producción eliminada. Estado vuelto a Presupuestado.',
    });
    setTimeout(() => setToast(null), 3500);
  };

  const abrirModalSalidaProduccion = () => {
    setHoraSalida((h) => normalizarHoraHHmm(h, HORA_CORTE_REPORTE));
    setAlmacenSalidaId((id) => id || defaultAlmacenId);
    setModalSalidaAbierto(true);
  };

  const handleHoraSalidaChange = (nextRaw) => {
    const next = normalizarHoraHHmm(nextRaw, HORA_CORTE_REPORTE);
    setHoraSalida(next);
    // Evita mostrar costos/stock de una hora previa mientras llega la nueva consulta.
    setStockCostByProduct({});
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [res, cfgRes, productsRes] = await Promise.all([
          getWarehouses({ active_only: true }),
          concesionariaWarehouseConfigService.list().catch(() => null),
          productService.getProducts({ active: true }).catch(() => null),
        ]);
        if (!active) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        const baseWarehouses = list.filter((w) => {
          const hasSourceWarehouse = w?.source_warehouse_id != null && String(w.source_warehouse_id).trim() !== '';
          const createdByAnyModule = Boolean(w?.created_for_module);
          return !hasSourceWarehouse && !createdByAnyModule;
        });
        setWarehousesOut(baseWarehouses);
        if (!almacenSalidaId && baseWarehouses[0]?.id != null) {
          setAlmacenSalidaId(String(baseWarehouses[0].id));
        }

        const cfgs = Array.isArray(cfgRes?.data?.configs) ? cfgRes.data.configs : [];
        const byPrincipal = {};
        cfgs.forEach((cfg) => {
          const principalId = cfg?.principal_warehouse_id ?? cfg?.principalWarehouse?.id ?? cfg?.principal_warehouse?.id;
          const finished = cfg?.finished_warehouse || cfg?.finishedWarehouse || null;
          if (principalId == null || !finished) return;
          byPrincipal[String(principalId)] = {
            id: finished.id,
            code: finished.code || '',
            name: finished.name || '',
          };
        });
        setWarehouseDestinationByPrincipal(byPrincipal);

        const productsRaw = Array.isArray(productsRes?.data)
          ? productsRes.data
          : (Array.isArray(productsRes?.data?.data) ? productsRes.data.data : []);
        const unitsMap = {};
        productsRaw.forEach((p) => {
          if (p?.id == null) return;
          const u = String(p?.unit_of_measure ?? '').trim();
          if (!u) return;
          unitsMap[String(p.id)] = u;
        });
        setProductUnitById(unitsMap);
      } catch {
        if (active) {
          setWarehousesOut([]);
          setWarehouseDestinationByPrincipal({});
          setProductUnitById({});
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const whId = almacenSalidaId || defaultAlmacenId;
    const horaCorte = normalizarHoraHHmm(horaSalida, HORA_CORTE_REPORTE);
    if (!whId || !ymd) {
      setStockCostByProduct({});
      return;
    }
    let cancel = false;
    setLoadingWarehouseStock(true);
    entryGuideService
      .getProductStockAndCostBulk(whId, null, ymd, horaCorte, true)
      .then((resp) => {
        if (cancel) return;
        if (resp?.success && resp?.data && typeof resp.data === 'object') {
          setStockCostByProduct(resp.data);
        } else {
          setStockCostByProduct({});
        }
      })
      .catch(() => {
        if (!cancel) setStockCostByProduct({});
      })
      .finally(() => {
        if (!cancel) setLoadingWarehouseStock(false);
      });
    return () => {
      cancel = true;
    };
  }, [almacenSalidaId, defaultAlmacenId, ymd, horaSalida]);

  /** Confirma salida desde el modal (hora + resumen) y persiste. */
  const confirmarSalidaProduccion = async () => {
    if (!cicloId || !esDiaKeyValido(diaKey)) return;
    const hora = String(horaSalida || '').trim().slice(0, 5);
    if (!/^\d{1,2}:\d{2}$/.test(hora)) {
      window.alert('Indique una hora de salida válida.');
      return;
    }
    const whPrincipal = String(almacenSalidaId || defaultAlmacenId || '');
    if (!whPrincipal) {
      window.alert('Seleccione un almacén principal de despacho.');
      return;
    }
    if (!destinoAlmacenSeleccionado?.id) {
      window.alert('El almacén principal no tiene almacén de destino configurado para concesionaria.');
      return;
    }
    if (!ymd) {
      window.alert('No se encontró la fecha del día para registrar la salida.');
      return;
    }
    if (!resumenInsumosKardex?.filas?.length) {
      window.alert('No hay insumos para generar la guía de salida.');
      return;
    }
    if (!resumenPlatosTerminados?.rows?.length || resumenPlatosTerminados.qtyTotal <= 0) {
      window.alert('No hay platos producidos para generar la guía de entrada.');
      return;
    }

    const payload = {
      ciclo_id: cicloId,
      dia_key: diaKey,
      principal_warehouse_id: Number(whPrincipal),
      entry_date: ymd,
      issue_date: ymd,
      entry_time: normalizarHoraHHmm(hora, HORA_CORTE_REPORTE),
      project_id: ciclo?.proyectoId ? Number(ciclo.proyectoId) : null,
      insumos: resumenInsumosKardex.filas
        .filter((r) => r?.product_id != null && Number(r.quantity || 0) > 0)
        .map((r) => ({
          product_id: Number(r.product_id),
          description: r.product || null,
          project_id: ciclo?.proyectoId ? Number(ciclo.proyectoId) : null,
          quantity: Number(r.quantity || 0),
          unit_of_measure: r.unit || null,
          unit_price: Number(r.costoUnitarioKardex || 0),
          condition: 'good',
        })),
      platos: resumenPlatosTerminados.rows
        .filter((r) => r?.product_id != null && Number(r.quantity || 0) > 0)
        .map((r) => ({
          product_id: Number(r.product_id),
          description: r.product || null,
          project_id: ciclo?.proyectoId ? Number(ciclo.proyectoId) : null,
          quantity: Number(r.quantity || 0),
          unit_of_measure: null,
          unit_price: Number(r.unitPrice || 0),
          condition: 'good',
        })),
    };

    setConfirmandoSalida(true);
    try {
      const resp = await confirmarSalidaProduccionApi(payload);
      const salidaCode = resp?.data?.salida_guide_code || '—';
      const entradaCode = resp?.data?.entrada_guide_code || '—';
      const salidaId = resp?.data?.salida_guide_id ?? null;
      const entradaId = resp?.data?.entrada_guide_id ?? null;
      setEstado(ESTADO_SALIDA.SALIDA_PRODUCCION);
      upsertSalidaDia({
        cicloId,
        diaKey,
        estado: ESTADO_SALIDA.SALIDA_PRODUCCION,
        cantidades,
        bloquearCantidades: true,
        horaSalidaProduccion: hora,
        salidaGuideId: salidaId,
        entradaGuideId: entradaId,
      });
      setBloquearCantidades(true);
      setSalidaGuideId(salidaId);
      setEntradaGuideId(entradaId);
      setModalSalidaAbierto(false);
      setToast({ type: 'ok', message: `Salida confirmada. Guías: ${salidaCode} (salida) y ${entradaCode} (entrada PT).` });
      setTimeout(() => setToast(null), 5000);
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'No se pudo confirmar la salida de producción.' });
      setTimeout(() => setToast(null), 5000);
      return;
    } finally {
      setConfirmandoSalida(false);
    }

  };

  const presupuestoGuardado = bloquearCantidades && estado === ESTADO_SALIDA.PRESUPUESTADO;
  const salidaProduccionGuardada = bloquearCantidades && estado === ESTADO_SALIDA.SALIDA_PRODUCCION;

  if (!esDiaKeyValido(diaKey)) {
    return <Navigate to={basePath} replace />;
  }

  if (cicloCargando) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full">
        <p className="text-sm text-gray-600">Cargando cíclico…</p>
      </div>
    );
  }

  if (!ciclo) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full">
        <p className="text-sm text-amber-800">No se encontró el cíclico.</p>
        <Link to={basePath} className="mt-4 inline-block text-teal-700 text-sm font-medium hover:underline">
          ← Volver al listado
        </Link>
      </div>
    );
  }

  if (!plantilla || !diaValidoEnPlantilla) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full">
        <p className="text-sm text-amber-800">
          Este día no está activo en la plantilla del cíclico o la plantilla no existe.
        </p>
        <Link to={basePath} className="mt-4 inline-block text-teal-700 text-sm font-medium hover:underline">
          ← Volver al listado
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full max-w-[1600px] mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-600">Operaciones</span>
        <span className="mx-2">/</span>
        <Link to={basePath} className="hover:text-teal-600">
          Salidas a producción
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Detalle</span>
      </nav>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Salida — {diaNombre}
            {ymd ? (
              <span className="font-normal text-gray-600 text-lg ml-2">
                ({formatearDiaCorto(ymd)} · {ymd})
              </span>
            ) : null}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-medium text-gray-800">{ciclo.nombre}</span>
            <span className="mx-2 text-gray-400">·</span>
            {etiquetaSemana(ciclo.anio, ciclo.numeroSemana)}
            <span className="mx-2 text-gray-400">·</span>
            {formatearRangoFechas(ciclo.fechaInicio, ciclo.fechaFin)}
          </p>
        </div>
        <Link
          to={basePath}
          className="text-sm text-teal-700 font-medium hover:underline shrink-0"
        >
          ← Volver al listado
        </Link>
      </div>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:flex-wrap lg:justify-between">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
            <span>
              <strong className="text-gray-800">Proyecto:</strong> {nombreProyecto(ciclo.proyectoId)}
            </span>
            <span>
              <strong className="text-gray-800">Plantilla:</strong> {plantilla.nombre}
            </span>
            <span>
              <strong className="text-gray-800">Costo total estimado (este día):</strong>{' '}
              <span className="text-teal-700 font-semibold">{fmtMoney(totalPresupuesto)}</span>
            </span>
            <span>
              <strong className="text-gray-800">Costo unitario (por plato, este día):</strong>{' '}
              <span className="text-teal-800 font-semibold">
                {fmtMoney(costoUnitarioPlatoDia)}
              </span>
              {totalPlatosDia === 0 && (
                <span className="text-gray-500 font-normal"> (indique cantidades de platos)</span>
              )}
            </span>
            {recetasDetalleCargando && (
              <span className="text-xs text-amber-700">Cargando insumos de recetas…</span>
            )}
            {salidaProduccionGuardada && (
              <span>
                <strong className="text-gray-800">Hora de salida:</strong>{' '}
                <span className="font-semibold text-emerald-800">
                  {horaSalida || '—'}
                </span>
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-3 min-w-0">
            {/* Paso 1: configuración — elegir estado y guardar como presupuesto */}
            {!bloquearCantidades && (
              <>
                <div>
                  <span className="block text-xs font-medium text-gray-600 mb-1">Estado al guardar</span>
                  {estado === ESTADO_SALIDA.PRESUPUESTADO ? (
                    <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-900">
                      {ESTADO_LABEL[ESTADO_SALIDA.PRESUPUESTADO]}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900">
                      {ESTADO_LABEL[ESTADO_SALIDA.SALIDA_PRODUCCION]}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={guardar}
                  className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700 shrink-0"
                >
                  Guardar
                </button>
              </>
            )}

            {/* Paso 2: ya guardado como presupuestado — dos acciones claras */}
            {presupuestoGuardado && (
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <span className="block text-xs font-medium text-gray-600">Estado actual</span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-900">
                    {ESTADO_LABEL[ESTADO_SALIDA.PRESUPUESTADO]}
                  </span>
                  <button
                    type="button"
                    onClick={habilitarEdicionCantidades}
                    className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Editar cantidades
                  </button>
                  <button
                    type="button"
                    onClick={abrirModalSalidaProduccion}
                    className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700"
                  >
                    Salida a producción
                  </button>
                </div>
              </div>
            )}

            {/* Ya en salida a producción (confirmado) */}
            {salidaProduccionGuardada && (
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <span className="block text-xs font-medium text-gray-600">Estado actual</span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900">
                    {ESTADO_LABEL[ESTADO_SALIDA.SALIDA_PRODUCCION]}
                  </span>
                  <button
                    type="button"
                    onClick={eliminarSalidaProduccion}
                    className="rounded-md border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-900 hover:bg-rose-100"
                  >
                    Eliminar salida a producción
                  </button>
                  <button
                    type="button"
                    onClick={habilitarEdicionCantidades}
                    className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Editar cantidades
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-500">
        {!bloquearCantidades ? (
          <>
            Indique las cantidades y pulse <strong>Guardar</strong> (se guarda como presupuesto). Después podrá usar{' '}
            <strong>Editar cantidades</strong> o <strong>Salida a producción</strong>.             La lista de insumos de cada plato <strong>solo se muestra si pulsa Ver</strong> (use <strong>Ocultar</strong>{' '}
            para cerrar). Los datos se preparan al cargar la pantalla, pero el desplegable es siempre bajo su control. Si
            un plato no coincide con el catálogo o no tiene costos, se muestra 0.
          </>
        ) : presupuestoGuardado ? (
          <>
            Cantidades en <strong>presupuesto</strong> (solo lectura). Elija <strong>Editar cantidades</strong> para
            ajustar o <strong>Salida a producción</strong> para confirmar el envío a cocina.
          </>
        ) : (
          <>
            <span className="text-gray-700 font-medium">Salida a producción confirmada.</span> Las cantidades están
            en solo lectura; use <strong>Editar cantidades</strong> si necesita corregirlas. Use <strong>Ver</strong> para
            el detalle por receta.
          </>
        )}
      </p>

      <div className="mt-4 rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-3 py-2">Tiempo</th>
                <th className="px-3 py-2">Tipo de plato</th>
                <th className="px-3 py-2 min-w-[180px]">Menú planificado</th>
                <th className="px-3 py-2 w-28">Cant. platos</th>
                <th className="px-3 py-2 w-32">Costo est.</th>
                <th className="px-3 py-2 w-24">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {slots.map((slot) => {
                const q = cantidades[slot.sk] ?? '';
                const costo = costoTotalCeldaPlatosKardex(q, slot.texto, recetasCatalogo, recetasFullById, stockCostByProduct);
                const partes = parseLineasCelda(slot.texto);
                const isOpen = expanded.has(slot.sk);
                return (
                  <React.Fragment key={slot.sk}>
                    <tr className="hover:bg-gray-50/80">
                      <td className="px-3 py-2 text-gray-800">{slot.tiempoLabel}</td>
                      <td className="px-3 py-2 text-gray-600">{slot.tipoLabel}</td>
                      <td className="px-3 py-2 text-gray-900">
                        {slot.texto && String(slot.texto).trim() && slot.texto !== '—' ? (
                          <span className="leading-snug">{slot.texto}</span>
                        ) : (
                          <span className="text-gray-400 italic">Sin menú</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {cantidadesEditables ? (
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={q}
                            onChange={(e) => setCantidad(slot.sk, e.target.value)}
                            className="w-full rounded border border-gray-300 px-2 py-1 text-right focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                          />
                        ) : (
                          <span
                            className="block w-full min-h-[2rem] rounded border border-gray-200 bg-gray-50 px-2 py-1 text-right tabular-nums text-gray-900"
                            title="Cantidad guardada (solo lectura)"
                          >
                            {q || '0'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-800">{fmtMoney(costo)}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => toggleExpand(slot.sk)}
                          disabled={partes.length === 0}
                          className="text-teal-700 text-xs font-medium hover:underline disabled:text-gray-400 disabled:no-underline"
                        >
                          {isOpen ? 'Ocultar' : 'Ver'}
                        </button>
                      </td>
                    </tr>
                    {isOpen && partes.length > 0 && (
                      <tr className="bg-gray-50/90">
                        <td colSpan={6} className="px-4 py-3">
                          <DetalleSlot
                            partes={partes}
                            cantidadPlatos={q}
                            recetasCatalogo={recetasCatalogo}
                            recetasFullById={recetasFullById}
                            stockCostByProduct={stockCostByProduct}
                            productUnitById={productUnitById}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalSalidaAbierto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-salida-titulo"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalSalidaAbierto(false);
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-teal-50/80">
              <h2 id="modal-salida-titulo" className="text-lg font-semibold text-gray-900">
                Confirmar salida a producción
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {ciclo?.nombre} · {diaNombre}
                {ymd ? ` · ${ymd}` : ''}
              </p>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="hora-salida-prod" className="block text-sm font-medium text-gray-700 mb-1">
                    Hora de salida a cocina / expedición
                  </label>
                  <input
                    id="hora-salida-prod"
                    type="time"
                    value={horaSalida}
                    onChange={(e) => handleHoraSalidaChange(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label htmlFor="almacen-salida-prod" className="block text-sm font-medium text-gray-700 mb-1">
                    Almacén de despacho
                  </label>
                  <select
                    id="almacen-salida-prod"
                    value={almacenSalidaId || defaultAlmacenId}
                    onChange={(e) => setAlmacenSalidaId(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500 bg-white"
                  >
                    {warehousesOut.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code ? `${a.code} - ${a.name}` : a.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Corte de kárdex para costos/stock: <strong>{ymd || '—'} {normalizarHoraHHmm(horaSalida, HORA_CORTE_REPORTE)}</strong>.
                  </p>
                  <p className="text-xs mt-1 text-gray-600">
                    <span className="font-medium">Almacén de destino:</span>{' '}
                    {destinoAlmacenSeleccionado
                      ? `${destinoAlmacenSeleccionado.code ? `${destinoAlmacenSeleccionado.code} - ` : ''}${destinoAlmacenSeleccionado.name}`
                      : 'No configurado para concesionaria'}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Resumen por menú (cantidades de platos)</h3>
                {resumenPlatosCeldas.length === 0 ? (
                  <p className="text-sm text-amber-700 bg-amber-50 rounded-md px-3 py-2">
                    No hay cantidades indicadas. Indique platos en la grilla antes de confirmar.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                          <th className="px-3 py-2">Tiempo</th>
                          <th className="px-3 py-2">Tipo</th>
                          <th className="px-3 py-2">Menú</th>
                          <th className="px-3 py-2 text-right">Cant. platos</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {resumenPlatosCeldas.map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50/80">
                            <td className="px-3 py-2 text-gray-800">{row.tiempo}</td>
                            <td className="px-3 py-2 text-gray-600">{row.tipo}</td>
                            <td className="px-3 py-2 text-gray-900">{row.menu}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">{row.cantidadPlatos}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Insumos / productos agregados (suma total del día)
                </h3>
                {resumenInsumosKardex.filas.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin insumos calculados para las cantidades actuales.</p>
                ) : (
                  <>
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                            <th className="px-3 py-2">Producto / insumo</th>
                            <th className="px-3 py-2">Unidad</th>
                            <th className="px-3 py-2 text-right">Cantidad total</th>
                            <th className="px-3 py-2 text-right whitespace-nowrap" title="Costo unitario desde kardex al corte del día">
                              C.unit
                            </th>
                            <th className="px-3 py-2 text-right whitespace-nowrap" title="Stock al corte del día (kardex)">
                              Stock
                            </th>
                            <th className="px-3 py-2 text-right">Subtotal est.</th>
                            <th className="px-3 py-2 text-center">Detalle</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {resumenInsumosKardex.filas.map((row, i) => {
                            const detailKey = row.product_id != null
                              ? `id:${String(row.product_id)}|||${row.unit}`
                              : `name:${row.product}|||${row.unit}`;
                            const detailRows = desgloseInsumoPorPlato.get(detailKey) || [];
                            return (
                              <tr key={`${row.product}-${row.unit}-${i}`} className="hover:bg-gray-50/80">
                                <td className="px-3 py-2 text-gray-900">{row.product}</td>
                                <td className="px-3 py-2 text-gray-600">{row.unit}</td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                  {formatearCantidadReceta4Dec(row.quantity)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-gray-800">
                                  {fmtMoney(row.costoUnitarioKardex)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-gray-800">
                                  {row.stockKardex != null ? Number(row.stockKardex).toLocaleString('es-PE', { maximumFractionDigits: 3 }) : '—'}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(row.subtotalKardex)}</td>
                                <td className="px-3 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setInsumoDetalleModal({
                                        product: row.product,
                                        unit: row.unit,
                                        totalQuantity: row.quantity,
                                        totalCost: row.subtotalKardex,
                                        detailRows,
                                      })
                                    }
                                    disabled={detailRows.length === 0}
                                    className="text-xs font-medium text-teal-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                                  >
                                    Ver plato
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-teal-50/50 font-semibold">
                            <td colSpan={6} className="px-3 py-2 text-right text-gray-800">
                              Total de costos (kárdex al corte)
                            </td>
                            <td className="px-3 py-2 text-right text-teal-900">{fmtMoney(resumenInsumosKardex.totalCosto)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div className="mt-2 flex justify-end">
                      <div className="rounded-md border border-teal-200 bg-teal-50 px-3 py-1.5 text-sm font-semibold text-teal-900">
                        Total costos: {fmtMoney(resumenInsumosKardex.totalCosto)}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {loadingWarehouseStock
                        ? 'Actualizando costo unitario y stock desde kárdex…'
                        : 'Costo unitario y stock tomados desde kárdex para el almacén y corte indicados.'}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setModalSalidaAbierto(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarSalidaProduccion}
                disabled={resumenPlatosCeldas.length === 0 || confirmandoSalida}
                className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {confirmandoSalida ? 'Confirmando...' : 'Confirmar salida a producción'}
              </button>
            </div>
          </div>
        </div>
      )}

      {insumoDetalleModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-insumo-plato-titulo"
          onClick={(e) => {
            if (e.target === e.currentTarget) setInsumoDetalleModal(null);
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-teal-50/80">
              <h3 id="modal-insumo-plato-titulo" className="text-base font-semibold text-gray-900">
                Desglose por plato — {insumoDetalleModal.product}
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Unidad: {insumoDetalleModal.unit} · Cantidad total: {formatearCantidadReceta4Dec(insumoDetalleModal.totalQuantity)} ·
                {' '}Costo total: {fmtMoney(insumoDetalleModal.totalCost)}
              </p>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              {insumoDetalleModal.detailRows.length === 0 ? (
                <p className="text-sm text-gray-500">No hay detalle por plato para este insumo.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                        <th className="px-3 py-2">Tiempo</th>
                        <th className="px-3 py-2">Plato</th>
                        <th className="px-3 py-2">Menú</th>
                        <th className="px-3 py-2 text-right">Cantidad</th>
                        <th className="px-3 py-2 text-right">C.unit</th>
                        <th className="px-3 py-2 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {insumoDetalleModal.detailRows.map((d, idx) => (
                        <tr key={`${d.tiempo}-${d.plato}-${idx}`} className="hover:bg-gray-50/80">
                          <td className="px-3 py-2 text-gray-800">{d.tiempo}</td>
                          <td className="px-3 py-2 text-gray-900">{d.plato}</td>
                          <td className="px-3 py-2 text-gray-600">{d.menu}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatearCantidadReceta4Dec(d.quantity)} {d.unit}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(d.cUnit)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(d.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-teal-50/50 font-semibold">
                        <td colSpan={5} className="px-3 py-2 text-right text-gray-800">Total insumo</td>
                        <td className="px-3 py-2 text-right text-teal-900">{fmtMoney(insumoDetalleModal.totalCost)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setInsumoDetalleModal(null)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          className="fixed bottom-6 right-6 rounded-md bg-gray-900 text-white px-4 py-2 text-sm shadow-lg z-50"
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

function DetalleSlot({ partes, cantidadPlatos, recetasCatalogo, recetasFullById, stockCostByProduct, productUnitById }) {
  const n = Math.max(0, parseInt(String(cantidadPlatos || '').replace(/\D/g, ''), 10) || 0);
  const m = recetasFullById instanceof Map ? recetasFullById : new Map();
  return (
    <div className="space-y-4 text-sm">
      <p className="text-xs text-gray-500">
        <strong>Unitario (1 plato):</strong> insumos por una porción según la ficha de receta.{' '}
        <strong>Total a producir:</strong> unitario × platos indicados en la grilla (
        <strong>{n}</strong> {n === 1 ? 'plato' : 'platos'}). Costos = total × C.unit (kárdex al corte; fallback al
        costo de receta si no existe en kárdex).
      </p>
      {partes.map((nombrePlato, idx) => {
        const light = buscarRecetaPlatoEnCatalogo(nombrePlato, recetasCatalogo);
        const receta =
          light?.id != null
            ? m.get(String(light.id)) || (Array.isArray(light.items) && light.items.length ? light : null)
            : null;
        const ings = receta ? lineasInsumoEscaladosRecetaApi(receta, n) : [];
        const ingsWithCost = ings.map((row) => {
          const pid = row.product_id != null ? String(row.product_id) : null;
          const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[row.product_id]) : null;
          const canonicalUnit = pid ? (productUnitById?.[pid] || row.unit || '') : (row.unit || '');
          const cUnit = Number(cached?.average_cost ?? row.precioUnitario ?? 0);
          const safeCunit = Number.isFinite(cUnit) ? cUnit : 0;
          const subtotal = Math.round((Number(row.quantity || 0) * safeCunit) * 100) / 100;
          const baseRow = Math.round((Number(row.quantityUnitario || 0) * safeCunit) * 100) / 100;
          return { ...row, unit: canonicalUnit || row.unit, cUnit: safeCunit, subtotal, baseRow };
        });
        const base = Math.round(ingsWithCost.reduce((acc, row) => acc + (Number(row.baseRow) || 0), 0) * 100) / 100;
        const totalPlato = Math.round(ingsWithCost.reduce((acc, row) => acc + (Number(row.subtotal) || 0), 0) * 100) / 100;
        const tituloReceta = receta?.name || (light?.name && String(light.name)) || null;
        return (
          <div
            key={`${idx}-${nombrePlato}`}
            className="rounded-md border border-gray-200 bg-white p-3"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 pb-2 mb-2">
              <div>
                <span className="font-medium text-gray-900">{nombrePlato}</span>
                {light?.id != null && (
                  <span className="ml-2 text-xs text-gray-500">
                    Receta: {tituloReceta || '—'}
                    {receta?.id != null && (
                      <span className="text-gray-400"> (id {String(receta.id)})</span>
                    )}
                  </span>
                )}
                {light == null && (
                  <span className="ml-2 text-xs text-amber-800">
                    No hay receta en el catálogo con este nombre.
                  </span>
                )}
                {light != null && !receta && (
                  <span className="ml-2 text-xs text-amber-800">Cargando receta o sin ítems en ficha.</span>
                )}
              </div>
              <div className="text-xs text-gray-600">
                Costo base 1 porc.: <strong>{fmtMoney(base)}</strong>
                {' · '}
                Subtotal ({n} {n === 1 ? 'plato' : 'platos'}): <strong className="text-teal-800">{fmtMoney(totalPlato)}</strong>
              </div>
            </div>
            {ingsWithCost.length > 0 ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="text-left font-medium py-1">Insumo</th>
                    <th
                      className="text-right font-medium py-1 max-w-[7rem] whitespace-normal leading-tight"
                      title="Cantidad de insumo por 1 plato (ficha de receta)"
                    >
                      Unitario (1 plato)
                    </th>
                    <th
                      className="text-right font-medium py-1 max-w-[7rem] whitespace-normal leading-tight"
                      title="Unitario multiplicado por la cantidad de platos de la grilla"
                    >
                      Total ({n} {n === 1 ? 'plato' : 'platos'})
                    </th>
                    <th className="text-right font-medium py-1">C.unit</th>
                    <th className="text-right font-medium py-1">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {ingsWithCost.map((row, i) => {
                    return (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="py-1 pr-2 text-gray-800">{row.product}</td>
                        <td className="py-1 text-right tabular-nums text-gray-800">
                          {formatearCantidadReceta4Dec(row.quantityUnitario)} {row.unit}
                        </td>
                        <td className="py-1 text-right tabular-nums text-gray-900">
                          {formatearCantidadReceta4Dec(row.quantity)} {row.unit}
                        </td>
                        <td className="py-1 text-right tabular-nums text-gray-600">{fmtMoney(row.cUnit)}</td>
                        <td className="py-1 text-right tabular-nums font-medium text-gray-800">{fmtMoney(row.subtotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : receta && Array.isArray(receta.items) && receta.items.length === 0 ? (
              <p className="text-xs text-gray-500">Receta sin insumos; costo 0.</p>
            ) : !receta && light == null ? null : (
              <p className="text-xs text-gray-400">Sin detalle de insumos (costo 0).</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default SalidasProduccionDetalle;
